import { generateJSON } from "@tiptap/react"
import showdown from "showdown"

import type { Editor } from "@tiptap/core"
import type { EditorView } from "@tiptap/pm/view"
import type { Extensions } from "@tiptap/react"
import { unwrapBlockImages } from "./unwrap-block-images"

const MARKDOWN_PATTERNS = [
	/^#{1,6}\s+.+$/m, // Headers
	/\*\*(.+?)\*\*/g, // Bold
	/\*(.+?)\*/g, // Italic
	/~~(.+?)~~/g, // Strikethrough
	/\[(.+?)\]\((.+?)\)/g, // Links
	/^[\s]*[-*+]\s+/m, // Unordered lists
	/^[\s]*\d+\.\s+/m, // Ordered lists
	/```[\s\S]*?```/g, // Code blocks
	/`(.+?)`/g, // Inline code
	/^>\s+.+$/m, // Blockquotes
	/^\|(.+\|)+$/m, // Tables
	/^---+$/m, // Horizontal rules
]

export function isMarkdown(text: string): boolean {
	return MARKDOWN_PATTERNS.some((pattern) => {
		pattern.lastIndex = 0
		return pattern.test(text)
	})
}

export function markdownToHtml(text: string): string {
	const converter = new showdown.Converter({
		tables: true,
		strikethrough: true,
		simplifiedAutoLink: true,
		openLinksInNewWindow: true,
		// Showdown ends every code block with a newline, which the editor shows as an empty last line.
		omitExtraWLInCodeBlocks: true,
	})
	return unwrapBlockImages(converter.makeHtml(text))
}

/**
 * Turns a pasted markdown block into rich text. Returns false when the paste is
 * not markdown or lands inside a code block, where the source text is the point.
 */
export function insertMarkdownPaste({
	view,
	event,
	editor,
	extensions,
}: {
	view: EditorView
	event: ClipboardEvent
	editor: Editor | null | undefined
	extensions: Extensions
}): boolean {
	const text = event.clipboardData?.getData("text/plain")
	if (!text || !isMarkdown(text)) return false
	if (view.state.selection.$from.parent.type.name === "codeBlock") return false

	const json = generateJSON(markdownToHtml(text), extensions)
	editor?.commands.insertContent(json)
	return true
}
